import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type {
  ContactoAdicional,
  CrmActivity,
  CrmOpportunity,
  CrmPipeline,
  DetallesOportunidad,
  EditarOportunidad,
} from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { Icono } from './Iconos.js';
import { Modal } from './Modal.js';

/**
 * LA FICHA DE UNA OPORTUNIDAD, COMO EN VENTRY.
 *
 * El equipo viene de trabajar cada lead en una ficha con su contacto, sus
 * detalles, la propiedad, las tareas y las notas, cada cosa en su sección. Si
 * aquí la ficha es más pobre, el CRM de verdad sigue siendo el otro: se vuelve
 * a Ventry a mirar el teléfono del socio o el número de dormitorios, y lo que se
 * actualiza es lo que se mira.
 *
 * Así que tiene lo mismo, en el mismo orden, con los mismos nombres de campo.
 * Las secciones de datos se editan y se guardan juntas; las tareas y las notas
 * se añaden en el momento, como en Ventry.
 */

type Seccion = 'info' | 'propiedad' | 'tareas' | 'notas';

/** Lo que se edita en el formulario: la oportunidad en el vocabulario de la pantalla. */
interface Formulario {
  nombre: string;
  contactoNombre: string;
  contactoEmail: string;
  contactoTelefono: string;
  contactosAdicionales: ContactoAdicional[];
  pipelineId: string;
  stageId: string;
  estado: CrmOpportunity['estado'];
  motivoPerdida: string;
  valorEuros: string;
  responsableUserId: string;
  seguidores: string[];
  contactoEmpresa: string;
  origen: string;
  esperadoEn: string;
  etiquetas: string[];
  exclusivoUserId: string;
  detalles: DetallesOportunidad;
}

function desde(op: CrmOpportunity): Formulario {
  return {
    nombre: op.nombre,
    contactoNombre: op.contactoNombre ?? '',
    contactoEmail: op.contactoEmail ?? '',
    contactoTelefono: op.contactoTelefono ?? '',
    contactosAdicionales: op.contactosAdicionales,
    pipelineId: op.pipelineId,
    stageId: op.stageId,
    estado: op.estado,
    motivoPerdida: op.motivoPerdida ?? '',
    valorEuros: op.valorCents === null ? '' : String(op.valorCents / 100),
    responsableUserId: op.responsableUserId ?? '',
    seguidores: op.seguidores,
    contactoEmpresa: op.contactoEmpresa ?? '',
    origen: op.origen ?? '',
    esperadoEn: op.esperadoEn ?? '',
    etiquetas: op.etiquetas,
    exclusivoUserId: op.exclusivoUserId ?? '',
    detalles: op.detalles,
  };
}

const nulo = (v: string) => (v.trim() === '' ? null : v.trim());
const igual = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * SOLO LO QUE HA CAMBIADO. Mandar la ficha entera reescribiría campos que nadie
 * ha tocado —y alguno importado de Ventry no pasaría la validación de hoy—, y
 * dos personas editando a la vez se pisarían lo que no han cambiado.
 */
function cambios(antes: Formulario, ahora: Formulario, esDireccion: boolean): EditarOportunidad {
  const c: EditarOportunidad = {};
  if (ahora.nombre.trim() !== antes.nombre.trim()) c.nombre = ahora.nombre.trim();
  if (ahora.contactoNombre !== antes.contactoNombre) c.contactoNombre = nulo(ahora.contactoNombre);
  if (ahora.contactoEmail !== antes.contactoEmail) c.contactoEmail = nulo(ahora.contactoEmail);
  if (ahora.contactoTelefono !== antes.contactoTelefono) c.contactoTelefono = nulo(ahora.contactoTelefono);
  if (!igual(ahora.contactosAdicionales, antes.contactosAdicionales)) {
    c.contactosAdicionales = ahora.contactosAdicionales
      .map((x) => ({
        nombre: x.nombre.trim(),
        rol: nulo(x.rol ?? ''),
        email: nulo(x.email ?? ''),
        telefono: nulo(x.telefono ?? ''),
      }))
      .filter((x) => x.nombre);
  }
  if (ahora.pipelineId !== antes.pipelineId) c.pipelineId = ahora.pipelineId;
  if (ahora.stageId !== antes.stageId || ahora.pipelineId !== antes.pipelineId) c.stageId = ahora.stageId;
  if (ahora.estado !== antes.estado) c.estado = ahora.estado;
  if (ahora.motivoPerdida !== antes.motivoPerdida) c.motivoPerdida = nulo(ahora.motivoPerdida);
  if (ahora.valorEuros !== antes.valorEuros) {
    const euros = Number(ahora.valorEuros.replace(/\./g, '').replace(',', '.'));
    c.valorCents = ahora.valorEuros.trim() === '' ? null : Math.max(0, Math.round((Number.isFinite(euros) ? euros : 0) * 100));
  }
  if (ahora.responsableUserId !== antes.responsableUserId) c.responsableUserId = nulo(ahora.responsableUserId);
  if (!igual(ahora.seguidores, antes.seguidores)) c.seguidores = ahora.seguidores;
  if (ahora.contactoEmpresa !== antes.contactoEmpresa) c.contactoEmpresa = nulo(ahora.contactoEmpresa);
  if (ahora.origen !== antes.origen) c.origen = nulo(ahora.origen);
  if (ahora.esperadoEn !== antes.esperadoEn) c.esperadoEn = nulo(ahora.esperadoEn);
  if (!igual(ahora.etiquetas, antes.etiquetas)) c.etiquetas = ahora.etiquetas;
  if (esDireccion && ahora.exclusivoUserId !== antes.exclusivoUserId) {
    c.exclusivoUserId = nulo(ahora.exclusivoUserId);
  }
  if (!igual(ahora.detalles, antes.detalles)) c.detalles = ahora.detalles;
  return c;
}

// --- Las opciones de los desplegables: las de Ventry ------------------------------

/*
  Los valores son los de Ventry, tal cual se escriben allí: son datos, no
  textos de pantalla, y cambiarlos aquí partiría la correspondencia. Lo que una
  ficha traiga y no esté en la lista se conserva y se ofrece igual.
*/
const ENCAJE = ['1', '2', '3', '4', '5'];
const IDIOMAS = ['Español', 'English', 'Deutsch', 'Français', 'Italiano'];
const USOS = ['Habitual', 'Vacacional', 'Desocupado', 'Comercial', 'Inversión'];
const SUPERFICIES = ['Menos de 80 m²', '80 – 150 m²', '150 – 300 m²', 'Más de 300 m²'];
const ZONAS = ['Palma', 'Calvià', 'Andratx', 'Llucmajor', 'Resto de Mallorca'];
const TIPOS = ['Villa/Chalet', 'Finca', 'Apartamento', 'Ático', 'Adosado', 'Solar'];
const VISTAS = ['Mar', 'Montaña', 'Campo', 'Ciudad', 'Sin vistas'];

export function FichaOportunidad({
  idioma,
  oportunidad,
  embudos,
  esDireccion,
  onCambio,
  onCerrar,
}: {
  idioma: Idioma;
  oportunidad: CrmOpportunity;
  embudos: CrmPipeline[];
  esDireccion: boolean;
  onCambio: (op: CrmOpportunity) => void;
  onCerrar: () => void;
}) {
  const [op, setOp] = useState(oportunidad);
  const [form, setForm] = useState<Formulario>(() => desde(oportunidad));
  const [seccion, setSeccion] = useState<Seccion>('info');
  const [ocultarVacios, setOcultarVacios] = useState(false);
  const [personas, setPersonas] = useState<Array<{ id: string; nombre: string }>>([]);
  const [actividades, setActividades] = useState<CrmActivity[] | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState<{ texto: string; error: boolean } | null>(null);

  useEffect(() => {
    api
      .crmPersonas()
      .then((r) => setPersonas(r.personas))
      .catch(() => undefined);
    api
      .crmActividades(oportunidad.id)
      .then((r) => setActividades(r.actividades))
      .catch(() => setActividades([]));
  }, [oportunidad.id]);

  const original = useMemo(() => desde(op), [op]);
  const pendiente = cambios(original, form, esDireccion);
  const hayCambios = Object.keys(pendiente).length > 0;
  const embudo = embudos.find((e) => e.id === form.pipelineId);
  const nombreDe = (id: string | null) => personas.find((p) => p.id === id)?.nombre ?? null;

  function poner<K extends keyof Formulario>(campo: K, valor: Formulario[K]) {
    setForm((f) => ({ ...f, [campo]: valor }));
    setAviso(null);
  }
  function ponerDetalle(campo: keyof DetallesOportunidad, valor: string) {
    setForm((f) => ({ ...f, detalles: { ...f.detalles, [campo]: valor } }));
    setAviso(null);
  }

  async function guardar() {
    if (!hayCambios) return;
    if (form.nombre.trim().length < 2) {
      setAviso({ texto: t(idioma, 'crm.faltaNombre'), error: true });
      return;
    }
    setGuardando(true);
    try {
      const { oportunidad: nueva } = await api.crmEditarOportunidad(op.id, pendiente);
      setOp(nueva);
      setForm(desde(nueva));
      onCambio(nueva);
      setAviso({ texto: t(idioma, 'crm.ficha.guardado'), error: false });
    } catch (e) {
      setAviso({
        texto: e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'error.servidor'),
        error: true,
      });
    } finally {
      setGuardando(false);
    }
  }

  function cerrar() {
    // Cerrar con cambios sin guardar se pregunta: el fondo del diálogo se pulsa sin querer.
    if (hayCambios && !window.confirm(t(idioma, 'crm.ficha.descartarCambios'))) return;
    onCerrar();
  }

  /** Un campo, que se esconde si está vacío y se pidió ocultar los vacíos. */
  const campo = (etiqueta: string, valor: unknown, control: ReactNode, ancho = false) => {
    const vacio = valor === null || valor === undefined || valor === '' || (Array.isArray(valor) && valor.length === 0);
    if (ocultarVacios && vacio) return null;
    return (
      <div className={`campo${ancho ? ' ancho' : ''}`}>
        <span className="ficha-op-etiqueta">{etiqueta}</span>
        {control}
      </div>
    );
  };

  const texto = (valor: string, cambiar: (v: string) => void, opciones: { lista?: string; tipo?: string } = {}) => (
    <input
      type={opciones.tipo ?? 'text'}
      value={valor}
      list={opciones.lista}
      onChange={(e) => cambiar(e.target.value)}
    />
  );

  const desplegable = (valor: string, cambiar: (v: string) => void, lista: string[], vacio: string) => (
    <select value={valor} onChange={(e) => cambiar(e.target.value)}>
      <option value="">{vacio}</option>
      {/* Lo que vino de Ventry con otro valor se conserva. */}
      {valor && !lista.includes(valor) && <option value={valor}>{valor}</option>}
      {lista.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );

  const d = form.detalles;
  const det = (k: keyof DetallesOportunidad) => (d[k] ?? '') as string;

  return (
    <Modal idioma={idioma} titulo={op.nombre} icono="embudo" onCerrar={cerrar} variante="amplia">
      <div className="ficha-op">
        <nav className="ficha-op-menu" aria-label={op.nombre}>
          {(
            [
              ['info', 'crm.ficha.info'],
              ['propiedad', 'crm.ficha.propiedad'],
              ['tareas', 'crm.ficha.tareas'],
              ['notas', 'crm.ficha.notas'],
            ] as const
          ).map(([clave, texto]) => (
            <button
              key={clave}
              type="button"
              className={seccion === clave ? 'activa' : ''}
              aria-current={seccion === clave ? 'page' : undefined}
              onClick={() => setSeccion(clave)}
            >
              {t(idioma, texto)}
              {clave === 'tareas' && actividades && (
                <span className="ficha-op-cuenta">
                  {actividades.filter((a) => a.tipo !== 'NOTA' && !a.hecha).length || ''}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="ficha-op-cuerpo">
          {(seccion === 'info' || seccion === 'propiedad') && (
            <div className="ficha-op-barra">
              <h3>{t(idioma, seccion === 'info' ? 'crm.ficha.contacto' : 'crm.ficha.propiedad')}</h3>
              <label className="ficha-op-ocultar">
                <input type="checkbox" checked={ocultarVacios} onChange={(e) => setOcultarVacios(e.target.checked)} />
                {t(idioma, 'crm.ficha.ocultarVacios')}
              </label>
            </div>
          )}

          {/* --- Contacto y oportunidad ------------------------------------------- */}
          {seccion === 'info' && (
            <>
              <div className="ficha-op-rejilla">
                {campo(t(idioma, 'crm.ficha.contactoNombre'), form.contactoNombre, texto(form.contactoNombre, (v) => poner('contactoNombre', v)))}
                {campo(t(idioma, 'crm.ficha.email'), form.contactoEmail, texto(form.contactoEmail, (v) => poner('contactoEmail', v), { tipo: 'email' }))}
                {campo(
                  t(idioma, 'crm.ficha.telefono'),
                  form.contactoTelefono,
                  <span className="ficha-op-con-accion">
                    {texto(form.contactoTelefono, (v) => poner('contactoTelefono', v), { tipo: 'tel' })}
                    {form.contactoTelefono && (
                      <a className="boton secundario pequeno" href={`tel:${form.contactoTelefono.replace(/\s+/g, '')}`}>
                        {t(idioma, 'crm.ficha.llamar')}
                      </a>
                    )}
                  </span>,
                )}
              </div>

              {campo(
                t(idioma, 'crm.ficha.adicionales'),
                form.contactosAdicionales,
                <ContactosAdicionales
                  idioma={idioma}
                  lista={form.contactosAdicionales}
                  onCambio={(l) => poner('contactosAdicionales', l)}
                />,
                true,
              )}

              <h3 className="ficha-op-subtitulo">{t(idioma, 'crm.ficha.oportunidad')}</h3>
              <div className="ficha-op-rejilla">
                {campo(t(idioma, 'crm.ficha.nombre'), form.nombre, texto(form.nombre, (v) => poner('nombre', v)), true)}
                {campo(
                  t(idioma, 'crm.ficha.embudo'),
                  form.pipelineId,
                  <select
                    value={form.pipelineId}
                    onChange={(e) => {
                      // Otro embudo, otras columnas: se empieza por la primera.
                      const nuevo = embudos.find((x) => x.id === e.target.value);
                      setForm((f) => ({ ...f, pipelineId: e.target.value, stageId: nuevo?.etapas[0]?.id ?? '' }));
                    }}
                  >
                    {embudos.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.nombre}
                      </option>
                    ))}
                  </select>,
                )}
                {campo(
                  t(idioma, 'crm.ficha.etapa'),
                  form.stageId,
                  <select value={form.stageId} onChange={(e) => poner('stageId', e.target.value)}>
                    {(embudo?.etapas ?? []).map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nombre}
                      </option>
                    ))}
                  </select>,
                )}
                {campo(
                  t(idioma, 'crm.ficha.estado'),
                  form.estado,
                  <select value={form.estado} onChange={(e) => poner('estado', e.target.value as Formulario['estado'])}>
                    {(['ABIERTA', 'GANADA', 'PERDIDA'] as const).map((x) => (
                      <option key={x} value={x}>
                        {t(idioma, `crm.estado.${x}`)}
                      </option>
                    ))}
                  </select>,
                )}
                {campo(t(idioma, 'crm.ficha.valor'), form.valorEuros, texto(form.valorEuros, (v) => poner('valorEuros', v)))}
                {form.estado === 'PERDIDA' &&
                  campo(t(idioma, 'crm.ficha.motivoPerdida'), form.motivoPerdida, texto(form.motivoPerdida, (v) => poner('motivoPerdida', v)), true)}
                {campo(
                  t(idioma, 'crm.ficha.propietario'),
                  form.responsableUserId,
                  <select value={form.responsableUserId} onChange={(e) => poner('responsableUserId', e.target.value)}>
                    <option value="">{t(idioma, 'crm.ficha.sinAsignar')}</option>
                    {form.responsableUserId && !personas.some((p) => p.id === form.responsableUserId) && (
                      <option value={form.responsableUserId}>{op.responsableNombre ?? '—'}</option>
                    )}
                    {personas.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nombre}
                      </option>
                    ))}
                  </select>,
                )}
                {campo(
                  t(idioma, 'crm.ficha.seguidores'),
                  form.seguidores,
                  <Fichas
                    valores={form.seguidores}
                    etiqueta={(id) => nombreDe(id) ?? '—'}
                    opciones={personas.filter((p) => !form.seguidores.includes(p.id)).map((p) => ({ valor: p.id, texto: p.nombre }))}
                    anadir={t(idioma, 'crm.ficha.anadirSeguidor')}
                    quitar={t(idioma, 'crm.ficha.quitar')}
                    onCambio={(v) => poner('seguidores', v)}
                  />,
                )}
                {campo(t(idioma, 'crm.ficha.empresa'), form.contactoEmpresa, texto(form.contactoEmpresa, (v) => poner('contactoEmpresa', v)))}
                {campo(t(idioma, 'crm.ficha.fuente'), form.origen, texto(form.origen, (v) => poner('origen', v)))}
                {campo(t(idioma, 'crm.ficha.cierrePrevisto'), form.esperadoEn, texto(form.esperadoEn, (v) => poner('esperadoEn', v), { tipo: 'date' }))}
                {campo(
                  t(idioma, 'crm.ficha.etiquetas'),
                  form.etiquetas,
                  <Fichas
                    valores={form.etiquetas}
                    etiqueta={(e) => e}
                    libre
                    anadir={t(idioma, 'crm.ficha.anadirEtiqueta')}
                    quitar={t(idioma, 'crm.ficha.quitar')}
                    onCambio={(v) => poner('etiquetas', v)}
                  />,
                )}
                {campo(t(idioma, 'crm.ficha.encaje'), det('encajeCultural'), desplegable(det('encajeCultural'), (v) => ponerDetalle('encajeCultural', v), ENCAJE, '—'))}
                {campo(t(idioma, 'crm.ficha.idioma'), det('idioma'), desplegable(det('idioma'), (v) => ponerDetalle('idioma', v), IDIOMAS, '—'))}
                {campo(t(idioma, 'crm.ficha.usoReal'), det('usoReal'), desplegable(det('usoReal'), (v) => ponerDetalle('usoReal', v), USOS, '—'))}
                {campo(
                  t(idioma, 'crm.detalle.exclusiva'),
                  form.exclusivoUserId,
                  esDireccion ? (
                    <select value={form.exclusivoUserId} onChange={(e) => poner('exclusivoUserId', e.target.value)}>
                      <option value="">{t(idioma, 'crm.detalle.nadie')}</option>
                      {form.exclusivoUserId && !personas.some((p) => p.id === form.exclusivoUserId) && (
                        <option value={form.exclusivoUserId}>{op.exclusivoNombre ?? '—'}</option>
                      )}
                      {personas.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nombre}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="ficha-op-solo-lectura">{op.exclusivoNombre ?? t(idioma, 'crm.detalle.nadie')}</p>
                  ),
                )}
                {campo(
                  t(idioma, 'crm.ficha.riesgos'),
                  det('riesgos'),
                  <textarea rows={3} value={det('riesgos')} onChange={(e) => ponerDetalle('riesgos', e.target.value)} />,
                  true,
                )}
              </div>
            </>
          )}

          {/* --- La propiedad -------------------------------------------------------- */}
          {seccion === 'propiedad' && (
            <div className="ficha-op-rejilla">
              <datalist id="ficha-zonas">{ZONAS.map((z) => <option key={z} value={z} />)}</datalist>
              <datalist id="ficha-tipos">{TIPOS.map((z) => <option key={z} value={z} />)}</datalist>
              <datalist id="ficha-vistas">{VISTAS.map((z) => <option key={z} value={z} />)}</datalist>
              {campo(t(idioma, 'crm.ficha.zona'), det('zona'), texto(det('zona'), (v) => ponerDetalle('zona', v), { lista: 'ficha-zonas' }))}
              {campo(t(idioma, 'crm.ficha.superficie'), det('superficie'), desplegable(det('superficie'), (v) => ponerDetalle('superficie', v), SUPERFICIES, '—'))}
              {campo(t(idioma, 'crm.ficha.tipoPropiedad'), det('tipoPropiedad'), texto(det('tipoPropiedad'), (v) => ponerDetalle('tipoPropiedad', v), { lista: 'ficha-tipos' }))}
              {campo(t(idioma, 'crm.ficha.vistas'), det('vistas'), texto(det('vistas'), (v) => ponerDetalle('vistas', v), { lista: 'ficha-vistas' }))}
              {campo(t(idioma, 'crm.ficha.m2Solar'), det('m2Solar'), texto(det('m2Solar'), (v) => ponerDetalle('m2Solar', v)))}
              {campo(t(idioma, 'crm.ficha.m2Habitables'), det('m2Habitables'), texto(det('m2Habitables'), (v) => ponerDetalle('m2Habitables', v)))}
              {campo(t(idioma, 'crm.ficha.dormitorios'), det('dormitorios'), texto(det('dormitorios'), (v) => ponerDetalle('dormitorios', v), { tipo: 'number' }))}
              {campo(t(idioma, 'crm.ficha.banos'), det('banos'), texto(det('banos'), (v) => ponerDetalle('banos', v), { tipo: 'number' }))}
              {campo(t(idioma, 'crm.ficha.propuestoMax'), det('propuestoMax'), texto(det('propuestoMax'), (v) => ponerDetalle('propuestoMax', v)))}
              {campo(t(idioma, 'crm.ficha.estaEnLaIsla'), det('estaEnLaIsla'), texto(det('estaEnLaIsla'), (v) => ponerDetalle('estaEnLaIsla', v)))}
              {campo(t(idioma, 'crm.ficha.viveEnLaIsla'), det('viveEnLaIsla'), texto(det('viveEnLaIsla'), (v) => ponerDetalle('viveEnLaIsla', v)))}
            </div>
          )}

          {seccion === 'tareas' && (
            <Tareas
              idioma={idioma}
              oportunidadId={op.id}
              actividades={actividades}
              personas={personas}
              onCambio={setActividades}
            />
          )}

          {seccion === 'notas' && (
            <Notas idioma={idioma} oportunidadId={op.id} actividades={actividades} onCambio={setActividades} />
          )}
        </div>

        <footer className="ficha-op-pie">
          <p className="suave">
            {op.creadoPorNombre && (
              <>
                {t(idioma, 'crm.ficha.creadoPor', { nombre: op.creadoPorNombre })}
                <br />
              </>
            )}
            {t(idioma, 'crm.ficha.creadoEl', { fecha: op.creadoEn.slice(0, 10) })} · {op.codigo}
          </p>
          {aviso && (
            <p className={aviso.error ? 'error' : 'aviso-ok'} role={aviso.error ? 'alert' : 'status'}>
              {aviso.texto}
            </p>
          )}
          {(seccion === 'info' || seccion === 'propiedad') && (
            <div className="ficha-op-botones">
              <button type="button" className="secundario" onClick={cerrar}>
                {t(idioma, 'app.cancelar')}
              </button>
              <button type="button" disabled={!hayCambios || guardando} onClick={() => void guardar()}>
                {t(idioma, 'app.guardar')}
              </button>
            </div>
          )}
        </footer>
      </div>
    </Modal>
  );
}

// --- Piezas -------------------------------------------------------------------------

/** Una lista de fichas con su aspa, y un selector o un campo para añadir. */
function Fichas({
  valores,
  etiqueta,
  opciones,
  libre,
  anadir,
  quitar,
  onCambio,
}: {
  valores: string[];
  etiqueta: (v: string) => string;
  opciones?: Array<{ valor: string; texto: string }>;
  libre?: boolean;
  anadir: string;
  quitar: string;
  onCambio: (v: string[]) => void;
}) {
  const [nuevo, setNuevo] = useState('');
  return (
    <div className="fichas-editables">
      {valores.map((v) => (
        <span key={v} className="ficha-chip">
          {etiqueta(v)}
          <button type="button" aria-label={`${quitar}: ${etiqueta(v)}`} onClick={() => onCambio(valores.filter((x) => x !== v))}>
            <Icono nombre="cerrar" />
          </button>
        </span>
      ))}
      {libre ? (
        <input
          value={nuevo}
          placeholder={anadir}
          aria-label={anadir}
          onChange={(e) => setNuevo(e.target.value)}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ',') && nuevo.trim()) {
              e.preventDefault();
              if (!valores.includes(nuevo.trim())) onCambio([...valores, nuevo.trim()]);
              setNuevo('');
            }
          }}
          onBlur={() => {
            if (nuevo.trim() && !valores.includes(nuevo.trim())) onCambio([...valores, nuevo.trim()]);
            setNuevo('');
          }}
        />
      ) : (
        (opciones ?? []).length > 0 && (
          <select
            value=""
            aria-label={anadir}
            onChange={(e) => {
              if (e.target.value) onCambio([...valores, e.target.value]);
            }}
          >
            <option value="">{anadir}</option>
            {(opciones ?? []).map((o) => (
              <option key={o.valor} value={o.valor}>
                {o.texto}
              </option>
            ))}
          </select>
        )
      )}
    </div>
  );
}

function ContactosAdicionales({
  idioma,
  lista,
  onCambio,
}: {
  idioma: Idioma;
  lista: ContactoAdicional[];
  onCambio: (l: ContactoAdicional[]) => void;
}) {
  const cambiar = (i: number, campo: keyof ContactoAdicional, valor: string) =>
    onCambio(lista.map((c, j) => (j === i ? { ...c, [campo]: valor } : c)));
  return (
    <div className="contactos-adicionales">
      {lista.map((c, i) => (
        <div key={i} className="contacto-adicional">
          <input value={c.nombre} placeholder={t(idioma, 'crm.ficha.contactoNombre')} aria-label={t(idioma, 'crm.ficha.contactoNombre')} onChange={(e) => cambiar(i, 'nombre', e.target.value)} />
          <input value={c.rol ?? ''} placeholder={t(idioma, 'crm.ficha.rol')} aria-label={t(idioma, 'crm.ficha.rol')} onChange={(e) => cambiar(i, 'rol', e.target.value)} />
          <input value={c.telefono ?? ''} type="tel" placeholder={t(idioma, 'crm.ficha.telefono')} aria-label={t(idioma, 'crm.ficha.telefono')} onChange={(e) => cambiar(i, 'telefono', e.target.value)} />
          <input value={c.email ?? ''} type="email" placeholder={t(idioma, 'crm.ficha.email')} aria-label={t(idioma, 'crm.ficha.email')} onChange={(e) => cambiar(i, 'email', e.target.value)} />
          <button type="button" className="icono-solo secundario" aria-label={t(idioma, 'crm.ficha.quitar')} onClick={() => onCambio(lista.filter((_, j) => j !== i))}>
            <Icono nombre="cerrar" />
          </button>
        </div>
      ))}
      {lista.length < 10 && (
        <button type="button" className="enlace" onClick={() => onCambio([...lista, { nombre: '', rol: null, email: null, telefono: null }])}>
          + {t(idioma, 'crm.ficha.anadirContacto')}
        </button>
      )}
    </div>
  );
}

const hoy = () => new Date().toISOString().slice(0, 10);

function Tareas({
  idioma,
  oportunidadId,
  actividades,
  personas,
  onCambio,
}: {
  idioma: Idioma;
  oportunidadId: string;
  actividades: CrmActivity[] | null;
  personas: Array<{ id: string; nombre: string }>;
  onCambio: (a: CrmActivity[]) => void;
}) {
  const [busqueda, setBusqueda] = useState('');
  const [nueva, setNueva] = useState<{ titulo: string; vence: string; responsable: string; detalle: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lista = (actividades ?? []).filter((a) => a.tipo !== 'NOTA');
  const filtradas = lista.filter((a) => a.titulo.toLowerCase().includes(busqueda.trim().toLowerCase()));
  const pendientes = filtradas.filter((a) => !a.hecha).sort((a, b) => (a.vence ?? '9999').localeCompare(b.vence ?? '9999'));
  const hechas = filtradas.filter((a) => a.hecha);

  async function crear() {
    if (!nueva || nueva.titulo.trim().length < 2) return;
    setError(null);
    try {
      const { actividad } = await api.crmCrearActividad(oportunidadId, {
        tipo: 'TAREA',
        titulo: nueva.titulo.trim(),
        ...(nueva.vence ? { vence: nueva.vence } : {}),
        ...(nueva.responsable ? { responsableUserId: nueva.responsable } : {}),
        ...(nueva.detalle.trim() ? { detalle: nueva.detalle.trim() } : {}),
      });
      onCambio([actividad, ...(actividades ?? [])]);
      setNueva(null);
    } catch (e) {
      setError(e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'error.servidor'));
    }
  }

  async function marcar(a: CrmActivity, hecha: boolean) {
    try {
      await api.crmCompletarActividad(a.id, hecha);
      onCambio((actividades ?? []).map((x) => (x.id === a.id ? { ...x, hecha } : x)));
    } catch {
      setError(t(idioma, 'error.servidor'));
    }
  }

  const fila = (a: CrmActivity) => (
    <li key={a.id} className={`ficha-op-tarea${a.hecha ? ' hecha' : ''}${!a.hecha && a.vence && a.vence < hoy() ? ' vencida' : ''}`}>
      <input type="checkbox" checked={a.hecha} aria-label={a.titulo} onChange={(e) => void marcar(a, e.target.checked)} />
      <span className="crecer">
        <b>{a.titulo}</b>
        <small className="suave">
          {[a.vence, a.responsableNombre].filter(Boolean).join(' · ') || t(idioma, 'crm.ficha.sinFechaNiResponsable')}
        </small>
        {a.detalle && <span className="ficha-op-texto">{a.detalle}</span>}
      </span>
    </li>
  );

  return (
    <div className="ficha-op-seccion">
      <div className="ficha-op-barra">
        <h3>{t(idioma, 'crm.ficha.tareas')}</h3>
      </div>
      {nueva ? (
        <div className="ficha-op-nueva">
          <div className="campo">
            <span className="ficha-op-etiqueta">{t(idioma, 'crm.ficha.tituloTarea')}</span>
            <input autoFocus value={nueva.titulo} onChange={(e) => setNueva({ ...nueva, titulo: e.target.value })} />
          </div>
          <div className="ficha-op-rejilla">
            <div className="campo">
              <span className="ficha-op-etiqueta">{t(idioma, 'crm.ficha.vence')}</span>
              <input type="date" value={nueva.vence} onChange={(e) => setNueva({ ...nueva, vence: e.target.value })} />
            </div>
            <div className="campo">
              <span className="ficha-op-etiqueta">{t(idioma, 'crm.ficha.responsableTarea')}</span>
              <select value={nueva.responsable} onChange={(e) => setNueva({ ...nueva, responsable: e.target.value })}>
                <option value="">{t(idioma, 'crm.ficha.sinAsignar')}</option>
                {personas.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="campo">
            <span className="ficha-op-etiqueta">{t(idioma, 'crm.ficha.detalleTarea')}</span>
            <textarea rows={2} value={nueva.detalle} onChange={(e) => setNueva({ ...nueva, detalle: e.target.value })} />
          </div>
          <p className="suave pequeno">{t(idioma, 'crm.ficha.tareaEnPanel')}</p>
          <div className="ficha-op-botones">
            <button type="button" className="secundario" onClick={() => setNueva(null)}>
              {t(idioma, 'app.cancelar')}
            </button>
            <button type="button" disabled={nueva.titulo.trim().length < 2} onClick={() => void crear()}>
              {t(idioma, 'app.guardar')}
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="ficha-op-anadir" onClick={() => setNueva({ titulo: '', vence: '', responsable: '', detalle: '' })}>
          + {t(idioma, 'crm.ficha.anadirTarea')}
        </button>
      )}
      {error && <p className="error" role="alert">{error}</p>}
      <input className="ficha-op-buscar" type="search" value={busqueda} placeholder={t(idioma, 'crm.ficha.buscarTarea')} aria-label={t(idioma, 'crm.ficha.buscarTarea')} onChange={(e) => setBusqueda(e.target.value)} />
      {actividades === null && <p>{t(idioma, 'app.cargando')}</p>}
      {actividades !== null && lista.length === 0 && <p className="suave">{t(idioma, 'crm.ficha.sinTareas')}</p>}
      <ul className="ficha-op-lista">
        {pendientes.map(fila)}
        {hechas.map(fila)}
      </ul>
    </div>
  );
}

function Notas({
  idioma,
  oportunidadId,
  actividades,
  onCambio,
}: {
  idioma: Idioma;
  oportunidadId: string;
  actividades: CrmActivity[] | null;
  onCambio: (a: CrmActivity[]) => void;
}) {
  const [busqueda, setBusqueda] = useState('');
  const [nueva, setNueva] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const q = busqueda.trim().toLowerCase();
  const notas = (actividades ?? [])
    .filter((a) => a.tipo === 'NOTA')
    .filter((a) => !q || `${a.titulo} ${a.detalle ?? ''}`.toLowerCase().includes(q))
    .sort((a, b) => b.creadoEn.localeCompare(a.creadoEn));

  async function guardar() {
    const texto = (nueva ?? '').trim();
    if (texto.length < 2) return;
    setError(null);
    try {
      const { actividad } = await api.crmCrearActividad(oportunidadId, {
        tipo: 'NOTA',
        titulo: texto.split('\n')[0]!.slice(0, 120),
        detalle: texto,
      });
      onCambio([actividad, ...(actividades ?? [])]);
      setNueva(null);
    } catch (e) {
      setError(e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'error.servidor'));
    }
  }

  return (
    <div className="ficha-op-seccion">
      <div className="ficha-op-barra">
        <h3>{t(idioma, 'crm.ficha.notas')}</h3>
        <input className="ficha-op-buscar" type="search" value={busqueda} placeholder={t(idioma, 'crm.ficha.buscarNotas')} aria-label={t(idioma, 'crm.ficha.buscarNotas')} onChange={(e) => setBusqueda(e.target.value)} />
      </div>
      {nueva !== null ? (
        <div className="ficha-op-nueva">
          <textarea autoFocus rows={5} value={nueva} placeholder={t(idioma, 'crm.detalle.nuevaNota')} aria-label={t(idioma, 'crm.detalle.nuevaNota')} onChange={(e) => setNueva(e.target.value)} />
          <div className="ficha-op-botones">
            <button type="button" className="secundario" onClick={() => setNueva(null)}>
              {t(idioma, 'app.cancelar')}
            </button>
            <button type="button" disabled={(nueva ?? '').trim().length < 2} onClick={() => void guardar()}>
              {t(idioma, 'crm.detalle.guardarNota')}
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="ficha-op-anadir" onClick={() => setNueva('')}>
          + {t(idioma, 'crm.detalle.nuevaNota')}
        </button>
      )}
      {error && <p className="error" role="alert">{error}</p>}
      {actividades === null && <p>{t(idioma, 'app.cargando')}</p>}
      {actividades !== null && notas.length === 0 && <p className="suave">{t(idioma, 'crm.detalle.sinHistorial')}</p>}
      <ul className="ficha-op-lista">
        {notas.map((a) => (
          <li key={a.id} className="ficha-op-nota">
            <p className="ficha-op-texto">{a.detalle?.trim() || a.titulo}</p>
            <small className="suave">
              {new Date(a.creadoEn).toLocaleString(idioma, { dateStyle: 'medium', timeStyle: 'short' })}
              {a.autorNombre ? ` · ${t(idioma, 'crm.ficha.creadoPor', { nombre: a.autorNombre })}` : ''}
            </small>
          </li>
        ))}
      </ul>
    </div>
  );
}
